import React, { useCallback, useEffect, useRef } from 'react';
import { DotLottieReact as DotLottieReactRaw } from '@lottiefiles/dotlottie-react';
import type { DotLottie, DotLottieReactProps } from '@lottiefiles/dotlottie-react';

import { usePrefersReduced } from '../motion';

// The package is authored against React 19 types, where a component may return
// `ReactNode`. Under this project's React 18 JSX typings that signature is not a
// valid JSX element, so re-cast it to a plain FC of its own props.
const DotLottieReact = DotLottieReactRaw as unknown as React.FC<DotLottieReactProps>;

/**
 * Stable Lottie icon names. Each maps to a `.lottie` asset served from
 * `public/lottie/<File>.lottie` (note the exact PascalCase filenames). The
 * union key is the lowercased concept; the value is the on-disk filename.
 */
export type LottieName =
  | 'product'
  | 'delivery'
  | 'gears'
  | 'meeting'
  | 'construction'
  | 'forklift'
  | 'scale'
  | 'money'
  | 'creditcard'
  | 'handshake'
  | 'notebook'
  | 'checklist'
  | 'complex'
  | 'scan_document'
  | 'classroom';

const LOTTIE_FILES: Record<LottieName, string> = {
  product: 'Product',
  delivery: 'Delivery',
  gears: 'Gears',
  meeting: 'Meeting',
  construction: 'Construction',
  forklift: 'Forklift',
  scale: 'Scale',
  money: 'Money',
  creditcard: 'CreditCard',
  handshake: 'Handshake',
  notebook: 'Notebook',
  checklist: 'Checklist',
  complex: 'ComplexServices',
  scan_document: 'ScanDocument',
  classroom: 'Classroom',
};

/** Resolve a `LottieName` to its public URL (`/lottie/<File>.lottie`). */
export const lottieSrc = (name: LottieName): string =>
  `/lottie/${LOTTIE_FILES[name]}.lottie`;

export interface LottieIconProps {
  /** One of the 8 stable Lottie names. Resolves to `/lottie/<File>.lottie`. */
  name: LottieName;
  /** Width & height in px (square). Defaults to 96. */
  size?: number;
  /**
   * Hover-driven playback flag. When `true` the animation plays from the start
   * and loops; when `false` it stops and rests on the LAST frame (the completed
   * state). Drive this from the containing card's hover state so the icon
   * animates while the card is hovered. Ignored when the user prefers reduced
   * motion (then it never plays and stays on the last frame).
   */
  playing?: boolean;
  /**
   * Play the animation ONCE when the icon first loads (in addition to the
   * hover-driven `playing` behavior), then rest on the last frame. A subtle,
   * tasteful "wake-up" on page load. Ignored when the user prefers reduced
   * motion. Defaults to `false`.
   */
  animateOnLoad?: boolean;
  /**
   * Delay (ms) before the on-load animation starts. Used to gently STAGGER a
   * grid of icons so they wake in sequence rather than all at once. Only
   * applies to the `animateOnLoad` pass. Defaults to 0.
   */
  loadDelayMs?: number;
  /** Passed through to the underlying player. */
  className?: string;
}

/**
 * Animated Lottie icon. Renders a `.dotLottie` via `DotLottieReact` with
 * `autoplay` disabled; playback is driven imperatively off the `playing` prop
 * (typically the card's hover state):
 *
 *  - `playing && !reducedMotion` → play from frame 0, looping.
 *  - otherwise                   → stop on the LAST frame (the completed /
 *    fully-assembled state), static.
 *
 * Resting on the last frame means an un-hovered icon shows its finished look
 * rather than an empty first frame. The total frame count is only known once
 * the asset loads, so we apply the rest frame on the `load` event as well as on
 * every `playing`/`reduced` change.
 *
 * Reduced motion is read via the motion system's `usePrefersReduced()` (the
 * single source of truth governed by `RESPECT_REDUCED_MOTION`); when it returns
 * true the icon stays on its last frame regardless of `playing`.
 */
export const LottieIcon: React.FC<LottieIconProps> = ({
  name,
  size = 96,
  playing = false,
  animateOnLoad = false,
  loadDelayMs = 0,
  className,
}) => {
  const reduced = usePrefersReduced();
  const dotLottieRef = useRef<DotLottie | null>(null);
  const src = lottieSrc(name);

  // On-load animation bookkeeping. Refs (not state) so the `load` callback can
  // read the latest values without re-subscribing, and so the one-shot guard
  // and pending timers survive re-renders. Timers are cleared on unmount.
  const animateOnLoadRef = useRef(animateOnLoad);
  const loadDelayRef = useRef(loadDelayMs);
  animateOnLoadRef.current = animateOnLoad;
  loadDelayRef.current = loadDelayMs;
  const hasAutoPlayedRef = useRef(false);
  const timersRef = useRef<number[]>([]);
  useEffect(
    () => () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current = [];
    },
    [],
  );

  // Park the (paused) instance on its last frame. The high-level dotLottie
  // `setFrame(frame)` both seeks AND renders that frame (per its API docs), so a
  // paused instance immediately repaints the completed last frame — no separate
  // `render()` is needed (nor exposed) on this class. Guarded because the frame
  // count is only known once the asset has loaded.
  const showLastFrame = useCallback((dl: DotLottie) => {
    try {
      const total = dl.totalFrames;
      if (!total || total <= 0) return;
      dl.pause();
      dl.setFrame(total - 1);
    } catch {
      /* instance not ready yet; the `load` listener will retry */
    }
  }, []);

  const setDotLottie = useCallback(
    (instance: DotLottie | null) => {
      const prev = dotLottieRef.current;
      if (prev && prev !== instance) {
        try {
          prev.removeEventListener('load');
        } catch {
          /* no-op */
        }
      }
      dotLottieRef.current = instance;
      if (!instance) return;
      // Once the asset's frames are known, park on the last frame (unless we are
      // actively playing this icon right now).
      const onLoad = () => {
        const isHoverPlaying = playingRef.current && !reducedRef.current;
        if (!isHoverPlaying) {
          showLastFrame(instance);
        }
        // One-time, subtle "wake-up" on first load: after a (possibly
        // staggered) delay, play through once and then rest on the last frame.
        // Skipped under reduced motion, when hover already drives playback, or
        // if it has already run for this icon.
        if (
          animateOnLoadRef.current &&
          !reducedRef.current &&
          !isHoverPlaying &&
          !hasAutoPlayedRef.current
        ) {
          hasAutoPlayedRef.current = true;
          const startTimer = window.setTimeout(() => {
            if (playingRef.current || reducedRef.current) return; // hover took over
            const dl = dotLottieRef.current;
            if (!dl) return;
            try {
              dl.setFrame(0);
              dl.play();
            } catch {
              /* not ready */
            }
            // Stop after a single cycle, then rest on the last frame — unless
            // hover has since taken ownership of playback.
            let durMs = 1400;
            try {
              const d = (dl as { duration?: number }).duration;
              if (typeof d === 'number' && d > 0) durMs = d * 1000;
            } catch {
              /* duration unavailable; use default */
            }
            const stopTimer = window.setTimeout(() => {
              const d2 = dotLottieRef.current;
              if (!d2) return;
              if (playingRef.current && !reducedRef.current) return; // hover owns it
              try {
                d2.stop();
              } catch {
                /* no-op */
              }
              showLastFrame(d2);
            }, durMs + 80);
            timersRef.current.push(stopTimer);
          }, loadDelayRef.current);
          timersRef.current.push(startTimer);
        }
      };
      try {
        instance.addEventListener('load', onLoad);
      } catch {
        /* no-op */
      }
      // If it is already loaded by the time the ref lands, apply immediately.
      try {
        if (instance.isLoaded) onLoad();
      } catch {
        /* no-op */
      }
    },
    [showLastFrame],
  );

  // Keep the latest playing/reduced values readable from the `load` callback
  // without re-subscribing the listener on every change.
  const playingRef = useRef(playing);
  const reducedRef = useRef(reduced);
  playingRef.current = playing;
  reducedRef.current = reduced;

  // Drive playback off `playing` (and reduced-motion). Runs on mount and on any
  // change so a late-loading instance still lands in the correct state.
  useEffect(() => {
    const dl = dotLottieRef.current;
    if (!dl) return;
    if (playing && !reduced) {
      // Restart from the beginning so each hover replays from frame 0.
      try {
        dl.setFrame(0);
      } catch {
        /* instance may not be ready; play() below still starts it */
      }
      dl.play();
    } else {
      // Stop and return to the LAST frame (the completed state), not frame 0.
      try {
        dl.stop();
      } catch {
        /* no-op if not ready */
      }
      showLastFrame(dl);
    }
  }, [playing, reduced, showLastFrame]);

  return (
    <DotLottieReact
      src={src}
      autoplay={false}
      loop
      className={className}
      dotLottieRefCallback={setDotLottie}
      style={{ width: size, height: size }}
    />
  );
};

export default LottieIcon;
